import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { PaymentService, RetailProvider, StripeService } from '@galaxy/billing';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ApproveEvent, OrderTermsComponent } from '@vendasta/orders';
import { ConfigInterface, Order, UserInterface } from '@vendasta/sales-orders';
import { OrderDetailAgreement } from '@vendasta/sales-ui';
import { OrderFormOptionsInterface } from '@vendasta/store';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';
import { OrderService, calculateReadOnlyOrderform } from '../../orders/order.service';
import { DeclineDialogComponent } from '../decline-dialog/decline-dialog.component';
import { OrdersService } from '../orders.service';
import { AccountGroup, AccountGroupService, ProjectionFilter } from '@galaxy/account-group';

// taken from the glxy-page options
enum displayModeType {
  absolute = 'absolute',
  block = 'block',
}

@Component({
  templateUrl: './order-approve-page.component.html',
  styleUrls: ['./order-approve-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OrderApprovePageComponent implements OnInit, OnDestroy {
  private loading$$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  loading$: Observable<boolean> = this.loading$$.asObservable();
  private error$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  error$: Observable<boolean> = this.error$$.asObservable();
  disableSubmitButton$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  disableSubmitButton$: Observable<boolean> = this.disableSubmitButton$$.asObservable();

  private _destroyNotifier$ = new Subject<void>();

  // partner branding
  logoUrl$: Observable<string>;

  // general order details
  order$: Observable<Order>;
  orderDetailAgreements$: Observable<OrderDetailAgreement[]>;
  orderFormOptions$: Observable<OrderFormOptionsInterface>;
  fileUploadUrl = OrderService.fileUploadUrl;
  users$: Observable<UserInterface[]>;

  retailProvider$: Observable<RetailProvider>;
  stripeKey: string;

  // terms and conditions
  orderConfig$: Observable<ConfigInterface>;
  partnerName$: Observable<string>;

  // Account group information
  title = '';
  subtitle = '';

  // 'absolute' display mode gives the ability to have a sticky header and footer,
  // whereas 'block' display mode is needed for print views to capture entire page
  displayMode: displayModeType = displayModeType.absolute;
  expandDropdowns = false;
  @ViewChild(OrderTermsComponent) terms: OrderTermsComponent;

  constructor(
    private route: ActivatedRoute,
    public snackbarService: SnackbarService,
    public dialog: MatDialog,
    public ordersService: OrdersService,
    private paymentService: PaymentService,
    private stripeService: StripeService,
    private accountGroupService: AccountGroupService,
  ) {}

  ngOnInit(): void {
    this.stripeKey = this.stripeService.getConnectPublicKey();

    if (this.route.snapshot.data.legacy) {
      this.route.paramMap
        .pipe(
          map((params) => {
            return params.get('orderId');
          }),
          filter((orderId: string) => !!orderId),
          takeUntil(this._destroyNotifier$),
        )
        .subscribe((orderId) => {
          this.ordersService.setHashedOrderId(orderId);
        });
    } else {
      const businessId = this.route.snapshot.paramMap.get('accountGroupId');
      const orderId = this.route.snapshot.paramMap.get('orderId');
      const authToken = this.route.snapshot.queryParamMap.get('token');
      this.ordersService.setOrderKeySet({ businessId, orderId, hashedOrderId: authToken });
    }

    this.order$ = this.ordersService.order$.pipe(
      catchError(() => {
        this.error$$.next(true);
        return of({} as Order);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    const accountGroup$ = this.order$.pipe(
      take(1),
      switchMap((order) => {
        return this.accountGroupService.get(order.businessId, new ProjectionFilter({ napData: true }));
      }),
    );

    this.users$ = this.ordersService.users$.pipe(
      map((usersObject) => {
        return Object.values(usersObject);
      }),
    );

    this.logoUrl$ = this.ordersService.getLogoUrl();

    this.orderConfig$ = this.ordersService.orderConfig$.pipe(shareReplay({ refCount: true, bufferSize: 1 }));

    this.partnerName$ = this.ordersService.partnerName$;

    combineLatest([this.order$, this.orderConfig$, accountGroup$])
      .pipe(takeUntil(this._destroyNotifier$))
      .subscribe(([_order, _orderConfig, accountGroup]) => {
        this.title = accountGroup.napData.companyName;
        this.subtitle = this.formatPageSubtitle(accountGroup);
        this.loading$$.next(false);
      });

    this.orderDetailAgreements$ = this.order$.pipe(
      map((order) => {
        return (order.agreements || []).map((a) => {
          // unable to fetch user data on public page so the usersMap is empty
          return OrderDetailAgreement.agreementFromOrderAgreementWithUser(a, {});
        });
      }),
    );

    this.orderFormOptions$ = this.order$.pipe(
      map((order) => {
        return {
          bypassRequiredQuestions: false,
          readOnly: calculateReadOnlyOrderform(order),
          showOfficeUseQuestions: false,
        };
      }),
    );

    this.retailProvider$ = this.order$.pipe(
      switchMap((order) => this.paymentService.getRetailProvider(order?.partnerId)),
      catchError(() => of(null)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  ngOnDestroy(): void {
    this._destroyNotifier$.next();
    this._destroyNotifier$.complete();
  }

  onApprove(approveEvent: ApproveEvent): void {
    this.disableSubmitButton$$.next(true);
    this.ordersService
      .updateAnswers(approveEvent.customForm, approveEvent.commonForm, approveEvent.extraFields)
      .pipe(
        switchMap(() => {
          return this.ordersService.approve(approveEvent.terms, approveEvent.paymentMethodToken);
        }),
        catchError((err) => {
          console.error(err);
          return of('');
        }),
        take(1),
      )
      .subscribe((orderId) => {
        if (orderId) {
          this.snackbarService.openSuccessSnack('STORE.ORDER_CONFIRMATION.APPROVED_MESSAGE');
          this.ordersService.reloadOrder();
        } else {
          this.disableSubmitButton$$.next(false);
          this.snackbarService.openErrorSnack('STORE.ORDER_CONFIRMATION.ERROR');
        }
      });
  }

  onDecline(_declineEvent: boolean): void {
    this.dialog
      .open(DeclineDialogComponent, {
        width: '680px',
      })
      .afterClosed()
      .pipe(
        filter((declinedOrder) => !!declinedOrder), // Ignore cancel closing the dialog
        take(1),
      )
      .subscribe(() => {
        this.snackbarService.openSuccessSnack('STORE.DECLINE_ORDER.SUCCESS');
        this.ordersService.reloadOrder();
      });
  }
  onPrint(orderId: string): void {
    this.displayMode = displayModeType.absolute;
    this.expandDropdowns = true;
    window.setTimeout(() => {
      document.title = orderId;
      window.print();
      this.expandDropdowns = false;
    }, 750);
  }

  formatPageSubtitle(business: AccountGroup): string {
    const address = [business.napData.address, business.napData.city, business.napData.state, business.napData.zip]
      .filter((item) => item)
      .join(', ');

    const primaryNumber: string | undefined = (business.napData.workNumber || [])[0];
    const phoneNumberItem = primaryNumber ? `phone number: ${primaryNumber}` : '';

    let customerIdItem = '';
    if (business.externalIdentifiers && business.externalIdentifiers.customerIdentifier) {
      customerIdItem = `customer identifier: ${business.externalIdentifiers.customerIdentifier}`;
    }

    return [address, phoneNumberItem, customerIdItem].filter((i) => i).join(' | ');
  }
}
